import { useCallback, useEffect, useState } from "react";

const STORAGE_KEY = "iwm-favorites";

function readFavorites(): string[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((item) => typeof item === "string") : [];
  } catch {
    return [];
  }
}

let favorites: string[] = [];
let initialized = false;
const listeners = new Set<() => void>();

function emit() {
  listeners.forEach((notify) => notify());
}

function ensureInit() {
  if (!initialized) {
    favorites = readFavorites();
    initialized = true;
  }
}

function persist(next: string[]) {
  favorites = next;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    /* storage unavailable - demo still works in memory */
  }
  emit();
}

export function useFavorites() {
  ensureInit();
  const [, forceUpdate] = useState(0);

  useEffect(() => {
    const notify = () => forceUpdate((value) => value + 1);
    listeners.add(notify);
    return () => {
      listeners.delete(notify);
    };
  }, []);

  const toggle = useCallback((id: string) => {
    ensureInit();
    persist(favorites.includes(id) ? favorites.filter((item) => item !== id) : [...favorites, id]);
  }, []);

  const isFavorite = useCallback((id: string) => favorites.includes(id), [favorites.length]);

  return { favorites, toggle, isFavorite };
}